/* Product-card profitability (merchant request 07/10/2026).
   Fills every [data-oms-profit] line from the calculator API (/v1/miners/profit, same maths as the calculator widget),
   owns the electricity-price dialog (snippets/oms-elec-dialog) and shares the chosen price with the widget:
   localStorage "oms_elec_cost" + window event "oms:elec-cost-change" {elecCost, source: "cards" | "widget"}.
   02/11/2026: Bitcoin home-miner cards carry a [data-oms-solo] line instead: the chance to find a full block within a
   year at today's difficulty, from /v1/solo/odds (same data and maths as /pages/solo-mining-calculator). */
const STORAGE_KEY = 'oms_elec_cost';
const SOLO_PAGE = '/pages/solo-mining-calculator';
const YEAR_DAYS = 365.25;
const DEFAULT_ELEC = String(window.OMS_ELEC_DEFAULT ?? '0');
const HOSTING_ELEC = String(window.OMS_ELEC_HOSTING ?? '0.06');
const CACHE_TTL_MS = 10 * 60 * 1000;
const state = {data: null, currency: null, elec: null, loading: null};

export function parseElec(value) {
  const raw = String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{0,6})?$/.test(raw)) return null;
  const cost = Number(raw);
  if (!Number.isFinite(cost) || cost < 0 || cost > 5) return null;
  return cost.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') || '0';
}

function storedElec() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw == null ? null : parseElec(raw);
  } catch (error) {
    return null;
  }
}

export function currentElec() {
  return storedElec() ?? parseElec(DEFAULT_ELEC) ?? '0';
}

function currentCurrency() {
  const value = String(window.OMS_CURRENCY || 'usd').toLowerCase();
  return value === 'eur' || value === 'chf' ? value : 'usd';
}

function symbol(currency) {
  return currency === 'eur' ? '€' : currency === 'chf' ? 'CHF ' : '$';
}

function money(amount, currency) {
  try {
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: 2, maximumFractionDigits: 2}).format(amount);
  } catch (error) {
    return symbol(currency) + amount.toFixed(2);
  }
}

export function elecLabel(elec, currency) {
  return `${symbol(currency)}${Number(elec).toFixed(2)}/kWh`;
}

async function fetchProfit(currency, elec) {
  const cacheKey = `oms_profit:${currency}:${elec}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.t < CACHE_TTL_MS) return cached.d;
  } catch (error) { /* no cache */ }
  const base = window.OMS_API_BASE;
  const apiKey = window.OMS_API_KEY;
  if (!base || !apiKey) throw new Error('calculator API not configured on this page');
  const url = new URL('/v1/miners/profit', base);
  url.searchParams.set('currency', currency);
  url.searchParams.set('elec_cost', elec);
  // Key in the query string like the calculator widget: a plain GET needs no CORS preflight, and the API's
  // dynamic CORS reads api_key from the URL to allow the storefront origin.
  url.searchParams.set('api_key', apiKey);
  const response = await fetch(url, {signal: AbortSignal.timeout(15000)});
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await response.json();
  const map = {};
  for (const miner of payload.miners || []) {
    map[miner.slug] = miner;
    if (miner.seo_handle) map[miner.seo_handle] = miner;
  }
  const data = {currency: payload.currency || currency, elec: String(payload.elec_cost ?? elec), updated: payload.updated_at, map};
  try { sessionStorage.setItem(cacheKey, JSON.stringify({t: Date.now(), d: data})); } catch (error) { /* storage full or blocked */ }
  return data;
}

function cardOf(element) {
  // The grid item carries the sort values (assets/oms-availability-filter.js reads them on the <li>).
  return element.closest('li.product-grid__item') || element.closest('product-card') || element;
}

function fill(data, scope = document) {
  const currency = data.currency;
  const elecText = `· ${elecLabel(data.elec, currency)}`;
  const fillKey = `${currency}:${data.elec}`;
  let filled = 0;
  for (const button of scope.querySelectorAll('[data-oms-profit]')) {
    if (button.dataset.omsFilled === fillKey) continue; // already up to date (re-inserted by a sort or a filter)
    button.dataset.omsFilled = fillKey;
    filled++;
    const miner = data.map[button.dataset.slug] || data.map[button.dataset.handle];
    const valueNode = button.querySelector('[data-oms-profit-value]');
    const elecNode = button.querySelector('[data-oms-profit-elec]');
    const card = cardOf(button);
    if (elecNode) elecNode.textContent = elecText;
    if (!miner || !miner.profitability_available || miner.net_profit == null) {
      button.dataset.state = 'unavailable';
      if (valueNode) valueNode.textContent = 'Profitability n/a';
      button.title = 'No profitability data for this miner yet. Click to change the electricity price.';
      delete card.dataset.omsNetDay;
      delete card.dataset.omsRoiDays;
      continue;
    }
    const net = Number(miner.net_profit);
    button.dataset.state = net >= 0 ? 'positive' : 'negative';
    if (valueNode) valueNode.textContent = `≈ ${net < 0 ? '−' : ''}${money(Math.abs(net), currency)} net/day`;
    card.dataset.omsNetDay = String(net);
    const payback = miner.roi_days != null && net > 0 ? Number(miner.roi_days) : NaN;
    card.dataset.omsRoiDays = Number.isFinite(payback) ? String(payback) : '';
    button.title = Number.isFinite(payback)
      ? `Estimated payback: ${Math.round(payback)} days at ${elecLabel(data.elec, currency)}. Click to change the electricity price.`
      : `Estimated at ${elecLabel(data.elec, currency)}. Click to change the electricity price.`;
  }
  if (filled) window.dispatchEvent(new CustomEvent('oms:profit-ready', {detail: {currency, elec: data.elec, filled}}));
}

export async function refresh(scope = document) {
  if (!document.querySelector('[data-oms-profit]')) return;
  const currency = currentCurrency();
  const elec = currentElec();
  if (state.data && state.currency === currency && state.elec === elec) {
    fill(state.data, scope);
    return;
  }
  if (state.loading && state.loading.key === `${currency}:${elec}`) return state.loading.promise;
  const promise = fetchProfit(currency, elec)
    .then((data) => {
      state.data = data;
      state.currency = currency;
      state.elec = elec;
      fill(data);
    })
    .catch((error) => {
      console.warn('Card profitability:', error.message);
      document.querySelectorAll('[data-oms-profit]').forEach((button) => {
        button.dataset.state = 'unavailable';
        const valueNode = button.querySelector('[data-oms-profit-value]');
        if (valueNode) valueNode.textContent = 'Profitability unavailable';
      });
    })
    .finally(() => { if (state.loading && state.loading.promise === promise) state.loading = null; });
  state.loading = {key: `${currency}:${elec}`, promise};
  return promise;
}

/* ---- solo "lottery" odds (Bitcoin home miners) ---- */
const solo = {data: null, loading: null};

async function fetchSolo() {
  const cacheKey = 'oms_solo_odds_v2';
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.t < CACHE_TTL_MS) return cached.d;
  } catch (error) { /* no cache */ }
  const base = window.OMS_API_BASE;
  const apiKey = window.OMS_API_KEY;
  if (!base || !apiKey) throw new Error('calculator API not configured on this page');
  const url = new URL('/v1/solo/odds', base);
  url.searchParams.set('currency', 'usd');
  url.searchParams.set('api_key', apiKey);
  const response = await fetch(url, {signal: AbortSignal.timeout(15000)});
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await response.json();
  const miners = {};
  for (const miner of payload.miners || []) {
    const perDay = miner.blocks_per_day && miner.blocks_per_day.btc;
    if (!(perDay > 0)) continue;
    const entry = {slug: miner.slug, perDay};
    miners[miner.slug] = entry;
    if (miner.seo_handle) miners[miner.seo_handle] = entry;
  }
  const btc = (payload.chains || []).find((chain) => chain.chain === 'btc');
  const data = {miners, jackpot: btc && btc.block_reward_value != null ? Number(btc.block_reward_value) : null};
  try { sessionStorage.setItem(cacheKey, JSON.stringify({t: Date.now(), d: data})); } catch (error) { /* storage full or blocked */ }
  return data;
}

// Same rounding and wording as the solo calculator (trimNum / big / odds there): "1 in N" while the chance is small,
// a percentage once it passes 25 %.
function trimNum(x) {
  if (x >= 1000) return Math.round(x).toLocaleString('en-US');
  const s = x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x.toFixed(2);
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

function big(n) {
  if (n < 10) return trimNum(n);
  if (n < 1e6) return Math.round(n).toLocaleString('en-US');
  for (const [size, word] of [[1e12, ' trillion'], [1e9, ' billion'], [1e6, ' million']]) if (n >= size) return trimNum(n / size) + word;
  return String(n);
}

function soloOdds(p) {
  if (p >= 0.25) return p * 100 >= 99.95 ? '> 99.9%' : `${(p * 100).toFixed(0)}%`;
  return `1 in ${big(1 / p)}`;
}

function fillSolo(data, scope = document) {
  for (const line of scope.querySelectorAll('[data-oms-solo]:not([data-oms-filled])')) {
    line.dataset.omsFilled = '1';
    const miner = data.miners[line.dataset.slug] || data.miners[line.dataset.handle];
    const valueNode = line.querySelector('[data-oms-solo-value]');
    const jackpotNode = line.querySelector('[data-oms-solo-jackpot]');  // product-page box only
    if (jackpotNode) jackpotNode.textContent = data.jackpot != null ? `≈ $${Math.round(data.jackpot).toLocaleString('en-US')}` : 'see calculator';
    if (!miner) {
      line.dataset.state = 'unavailable';
      if (valueNode) valueNode.textContent = 'see calculator';
      continue;
    }
    line.dataset.state = 'positive';
    line.dataset.omsSoloSlug = miner.slug;
    if (valueNode) valueNode.textContent = soloOdds(-Math.expm1(-miner.perDay * YEAR_DAYS));
  }
}

export async function refreshSolo(scope = document) {
  if (!document.querySelector('[data-oms-solo]:not([data-oms-filled])')) return;
  if (solo.data) { fillSolo(solo.data, scope); return; }
  if (solo.loading) return solo.loading;
  solo.loading = fetchSolo()
    .then((data) => { solo.data = data; fillSolo(data); })
    .catch((error) => {
      console.warn('Card solo odds:', error.message);
      document.querySelectorAll('[data-oms-solo-value], [data-oms-solo-jackpot]').forEach((node) => { node.textContent = 'see calculator'; });
    })
    .finally(() => { solo.loading = null; });
  return solo.loading;
}

function setupSoloLinks() {
  document.addEventListener('click', (event) => {
    const line = event.target instanceof Element ? event.target.closest('[data-oms-solo]') : null;
    if (!line) return;
    event.preventDefault();
    const slug = line.dataset.omsSoloSlug || line.dataset.slug;
    window.location.href = `${SOLO_PAGE}?miner=${encodeURIComponent(slug)}`;
  });
}

/* ---- electricity dialog ---- */
function setupDialog() {
  const dialog = document.getElementById('oms-elec-dialog');
  if (!dialog) return;
  const input = dialog.querySelector('[data-oms-elec-input]');
  const unit = dialog.querySelector('[data-oms-elec-unit]');
  const error = dialog.querySelector('[data-oms-elec-error]');
  const presets = [...dialog.querySelectorAll('[data-oms-elec-preset]')];
  const form = dialog.querySelector('[data-oms-elec-form]');

  function syncPresets() {
    const current = parseElec(input.value);
    presets.forEach((button) => {
      button.setAttribute('aria-pressed', current != null && parseElec(button.dataset.omsElecPreset) === current ? 'true' : 'false');
    });
  }
  function relabel() {
    const currency = currentCurrency();
    unit.textContent = `${symbol(currency).trim()}/kWh`;
    presets.forEach((button) => {
      if (button.hasAttribute('data-oms-elec-hosting')) button.textContent = `Our hosting: ${elecLabel(HOSTING_ELEC, currency)}`;
      else button.textContent = `${symbol(currency)}${Number(button.dataset.omsElecPreset).toFixed(2)}`;
    });
  }
  function open() {
    relabel();
    input.value = currentElec();
    error.hidden = true;
    syncPresets();
    if (typeof dialog.showModal === 'function') { if (!dialog.open) dialog.showModal(); } else dialog.setAttribute('open', '');
    setTimeout(() => { input.focus(); input.select(); }, 60);
  }
  function close() {
    if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
  }
  function apply(value) {
    const elec = parseElec(value);
    if (elec == null) { error.hidden = false; input.focus(); return false; }
    error.hidden = true;
    try { localStorage.setItem(STORAGE_KEY, elec); } catch (storageError) { /* private mode */ }
    window.dispatchEvent(new CustomEvent('oms:elec-cost-change', {detail: {elecCost: elec, source: 'cards'}}));
    close();
    refresh();
    return true;
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-oms-profit], [data-oms-elec-open]')) { event.preventDefault(); open(); return; }
    if (target.closest('[data-oms-elec-close]')) close();
  });
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  presets.forEach((button) => button.addEventListener('click', () => { input.value = button.dataset.omsElecPreset; error.hidden = true; syncPresets(); }));
  input.addEventListener('input', () => { error.hidden = true; syncPresets(); });
  dialog.querySelector('[data-oms-elec-reset]')?.addEventListener('click', () => apply(HOSTING_ELEC));
  form.addEventListener('submit', (event) => { event.preventDefault(); apply(input.value); });
}

/* ---- wiring ---- */
function watchNewCards() {
  const observer = new MutationObserver((mutations) => {
    let found = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element && (node.matches('[data-oms-profit], [data-oms-solo]') || node.querySelector('[data-oms-profit], [data-oms-solo]'))) { found = true; break; }
      }
      if (found) break;
    }
    if (!found) return;
    refreshSolo();
    const key = `${currentCurrency()}:${currentElec()}`;
    const pending = [...document.querySelectorAll('[data-oms-profit]')].some((button) => button.dataset.omsFilled !== key);
    if (!pending) return;
    if (state.data && state.currency === currentCurrency() && state.elec === currentElec()) fill(state.data);
    else refresh();
  });
  observer.observe(document.body, {childList: true, subtree: true});
}

function start() {
  setupDialog();
  setupSoloLinks();
  watchNewCards();
  window.addEventListener('oms:elec-cost-change', (event) => { if (event.detail?.source !== 'cards') refresh(); });
  window.addEventListener('oms:currency-change', () => refresh());
  // PSI2 26/10/2026: first API call after the browser's first paint (window.omsAfterFirstPaint, layout/theme.liquid)
  // rather than at module evaluation; same "net/day" lines, one frame later, outside the simulated LCP window.
  if (window.omsAfterFirstPaint) window.omsAfterFirstPaint(() => { refresh(); refreshSolo(); });
  else { refresh(); refreshSolo(); }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
else start();
